import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import {
  faChevronLeft,
  faChevronRight,
  faCircleExclamation,
  faTrashCanXmark,
} from "@fortawesome/pro-regular-svg-icons";
import moment, { MomentInput } from "moment";
import useModal from "hooks/modals/useModal";

import { showError, showSuccess } from "redux/actions/ui";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { formatDate, valueToString, monthNames, formatDateFromString } from "helpers/Helpers";

import { StatusCode } from "api/protocols";
import {
  GetTeeSheetOpenWindow,
  GetTeeSheets,
  ITeeSheet,
  PostTeeSheet,
  PutApply,
  PutTeeSheet,
  PutTeeSheetOpenWindow,
  GetBookingsPdf,
  GetTeeTime,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";

import { useAppDispatch, useAppSelector } from "hooks/redux";

import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Callout from "components/callout/Callout";
import { ButtonNew as Button } from "components/buttonNew";
import Popup from "components/popup/Popup";
import { Badge } from "components/badge/Badge";
import Page from "components/page/Page";
import { Select } from "components/select/index";
import SelectNew from "components/select/SelectNew";
import Sheet from "components/sheet/Sheet";
import DatePickerInput from "components/datePickerInput/DatePickerInput";

import "./teesheetCalendar.scss";
// import Tooltip from "components/tooltip/Tooltip";
import { useHistory } from "react-router";
import Spin from "components/spin/spin";
import TimePick from "components/timePick/TimePick";
import MonthPicker from "components/monthPicker/MonthPicker";
import RightClickMenu from "components/rightClickMenu/RightClickMenu";
import { GetTeeSheetTemplate, GetTeeSheetTemplatePrint } from "api/rpc/2024-04/facilityAdmin/teesheet/teesheetTemplate";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import DataTable from "components/dataTable/DataTable";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Checkbox from "components/form/checkbox/Checkbox";
import { clone } from "lodash";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";

interface ITemplate {
  end_time: string;
  facility_id: number;
  course_id: number;
  format: string;
  holes: number;
  id: number;
  interval: number;
  name: string;
  start_time: string;
}

interface IMainState {
  teesheets: ITeeSheet[];
  selectedTeeSheets: number[];
  applyTemplateActive: boolean;
  templates: ITemplate[];
  selectedTemplate: number;
  checked?: boolean | null;
  selectedCalendarDate: Date;
  courseId: number;
  viewOpenCloseTimes: boolean;
  markHolidays: boolean;
  holidaysAddPopupVisible: boolean;
  holidaysRemovePopupVisible: boolean;
  addTemplatePopupVisible: boolean;
  months: any[];
  years: any[];
  selectedQuickDateMonth: number;
  selectedQuickDateYear: number;
  reApplyWarningVisible: boolean;
  keepExistingBookings: boolean;
  keepExistingEvents: boolean;
}

interface ICreateTeeSheetState {
  selectedDate: string;
  modalVisible: boolean;
}

interface IOpenWindowState {
  editOpenWindowVisible: boolean;
  openTime: string;
  closeTime: string;
  openWindows: Record<string, any>;
  selectedOpenWindows: Array<number>;
}

interface ISelectedDate {
  date: string;
  tee_sheet_id: number;
  template_id: number;
  valid: boolean;
  day_of_week: string;
  note: string;
}

interface IWarningState {
  showWarningModal: boolean;
  league: ILeague;
  tournament: ITournament;
  eventDate: string;
}

export interface IApplyData {
  template_id: number;
  tee_sheet_id: number;
}

enum StateType {
  MainState = "IMainState",
}

const TeeSheetCalendar: React.FC = () => {
  const facilityStore = useAppSelector(store => store.facilityStore);

  const dispatch = useAppDispatch();
  const history = useHistory();
  const windowSize = useWindowSize();

  const { Option } = Select;
  const { t } = useTranslation();

  const [state, setState] = useState<IMainState>({
    teesheets: [],
    selectedTeeSheets: [],
    applyTemplateActive: false,
    templates: undefined,
    selectedTemplate: null,
    checked: null,
    selectedCalendarDate: new Date(),
    courseId: undefined,
    viewOpenCloseTimes: false,
    markHolidays: false,
    holidaysAddPopupVisible: false,
    holidaysRemovePopupVisible: false,
    addTemplatePopupVisible: false,
    months: [],
    years: [],
    selectedQuickDateMonth: new Date().getMonth() + 1,
    selectedQuickDateYear: new Date().getFullYear(),
    reApplyWarningVisible: false,
    keepExistingBookings: true,
    keepExistingEvents: true,
  });

  const [createTeeSheetState, setCreateTeeSheetState] = useState<ICreateTeeSheetState>({
    selectedDate: moment(new Date()).format("YYYY-MM").toString(),
    modalVisible: false,
  });

  const [openWindowState, setOpenWindowState] = useState<IOpenWindowState>({
    editOpenWindowVisible: false,
    openTime: "06:00",
    closeTime: "20:00",
    openWindows: {},
    selectedOpenWindows: [],
  });

  const [warningState, setWarningState] = useState<IWarningState>({
    showWarningModal: false,
    league: null,
    tournament: null,
    eventDate: "",
  });

  const {
    state: applyRangeModal,
    closeModal: closeApplyRangeModal,
    updateModal: updateApplyRangeModal,
  } = useModal({
    clearOnClose: true,
    date_range_start: new Date(),
    date_range_end: new Date(),
    date_range: null,
    default_template_id: null,
    monday_template_id: null,
    tuesday_template_id: null,
    wednesday_template_id: null,
    thursday_template_id: null,
    friday_template_id: null,
    saturday_template_id: null,
    sunday_template_id: null,
    days_of_week: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
    selected_dates: [] as ISelectedDate[],
    step: 1,
    apply_to_all_days: false,
    apply_to_specific_days: false,
    template_data: [],
    keep_existing_bookings: true,
    keep_existing_events: true,
  });

  const dayOfWeekOptions = [
    { label: t("Mondays"), value: "monday" },
    { label: t("Tuesdays"), value: "tuesday" },
    { label: t("Wednesdays"), value: "wednesday" },
    { label: t("Thursdays"), value: "thursday" },
    { label: t("Fridays"), value: "friday" },
    { label: t("Saturdays"), value: "saturday" },
    { label: t("Sundays"), value: "sunday" },
  ];

  // Filtered TeeSheet[] specific to calendar month and courseId
  const filteredTeeSheets = useMemo(() => {
    return (
      state.courseId !== undefined &&
      !isNaN(state.courseId) &&
      state.teesheets.length !== 0 &&
      state.teesheets.filter(teesheet => {
        const teesheetDate = new Date(teesheet?.date + " 00:00"); //capture date

        //determine related TeeSheets
        return (
          teesheetDate.toString() !== "Invalid Date" &&
          teesheetDate.getFullYear() === state.selectedCalendarDate.getFullYear() &&
          teesheetDate.getMonth() === state.selectedCalendarDate.getMonth() &&
          state.courseId === teesheet?.course_id
        );
      })
    );
  }, [state.selectedCalendarDate, state.courseId, state.teesheets]);

  useEffect(() => {
    void loadTeeSheets();
    void loadTeeSheetTemplates();
  }, []);

  useEffect(() => {
    const months: { month: any; id: number }[] = [];
    const years: { year: number; id: number }[] = [];

    monthNames(t).map((monthName: any, index: number) => {
      const month = {
        month: monthName,
        id: index + 1,
      };

      months.push(month);
    });

    let startingYear = new Date().getFullYear() - 5;
    const endingYear = new Date().getFullYear() + 5;

    for (let i = startingYear; i <= endingYear; i++) {
      const year = {
        year: startingYear,
        id: startingYear,
      };

      years.push(year);
      startingYear++;
    }

    setState(prevState => ({ ...prevState, months: months, years: years }));
  }, []);

  useEffect(() => {
    if (state.selectedCalendarDate && state.viewOpenCloseTimes) {
      void getOpenWindows();
    }
  }, [state.selectedCalendarDate, state.viewOpenCloseTimes]);

  useEffect(() => {
    if (state.courseId === undefined || isNaN(state.courseId)) {
      updateState({ courseId: facilityStore.facility?.courses?.[0]?.id }, StateType.MainState);
    }
  }, [facilityStore.facility]);

  async function getOpenWindows() {
    const windowsRes = await GetTeeSheetOpenWindow({ month: state.selectedCalendarDate.getMonth() + 1 }, true);
    if (windowsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting open windows")));
      return;
    }
    const formatOpenWindows = windowsRes?.data?.reduce(
      (formattedTest: { [key: string]: Record<string, any> }, currentDay: Record<string, any>) => {
        return { ...formattedTest, [currentDay.date]: currentDay };
      },
      {},
    );
    setOpenWindowState(prevState => ({ ...prevState, openWindows: formatOpenWindows }));
  }

  function updateState<T>(newState: Partial<T>, type: StateType) {
    const states = {
      [StateType.MainState]: (newState: Partial<T>) =>
        setState((cur: IMainState) => {
          return { ...cur, ...newState };
        }),
    };

    states[type](newState);
  }

  const onSelectedTeeSheetChange = (newId: number, teesheet?: any) => {
    if (newId === undefined || isNaN(newId)) {
      return;
    }

    if (state.selectedTeeSheets.some(selectedId => selectedId === newId)) {
      const updatedSelectedTeeSheets = state.selectedTeeSheets.filter(teesheetId => {
        if (teesheetId === newId) {
          return false;
        } else {
          return true;
        }
      });

      updateState<IMainState>({ selectedTeeSheets: updatedSelectedTeeSheets }, StateType.MainState);
    } else {
      //TODO: Remove checks once backend updated to queuing system
      //Only apply checks if 'Mark Holidays' is toggled off
      if (!state.markHolidays) {
        const selectedTeeSheetTemplateTitles = state.teesheets
          .filter(teesheet => state.selectedTeeSheets.includes(teesheet.id))
          .map(value => value.tee_sheet_template_title);

        // limit of 7 selected TeeSheets at a time.
        if (selectedTeeSheetTemplateTitles.length > 6) {
          dispatch(showError(t("Maximum of 7 days."), 2500));
          return;
        }

        // cannot select past dates
        if (
          moment(filteredTeeSheets.find(teeSheet => teeSheet.id === newId).date).isBefore(
            moment(new Date()).format("YYYY-MM-DD").toString(),
            "day",
          )
        ) {
          dispatch(showError(t("Cannot select past dates"), 2500));
          return;
        }

        // Always apply first selected tee sheet
        if (selectedTeeSheetTemplateTitles.length !== 0) {
          // limit to 1 teesheet if template found with the currently selected teesheet
          if (selectedTeeSheetTemplateTitles[0] !== null) {
            dispatch(showError(t("Only one tee sheet can be updated at a time"), 2500));
            return;
          }

          // disable select if currently selecting teesheets are null
          if (
            selectedTeeSheetTemplateTitles[0] === null &&
            filteredTeeSheets.find(teeSheet => teeSheet.id === newId).tee_sheet_template_title !== null
          ) {
            dispatch(showError(t("Can not apply templates at the same time as updating others")));
            return;
          }
        }
      }

      setWarningState(prevState => ({ ...prevState, eventDate: teesheet?.date }));
      updateState<IMainState>({ selectedTeeSheets: [...state.selectedTeeSheets, newId] }, StateType.MainState);
    }
  };

  const loadTeeSheets = async () => {
    try {
      const res = await GetTeeSheets({}, true);
      // load to state
      updateState<IMainState>({ teesheets: res.data }, StateType.MainState);
    } catch (e) {
      console.log("load tee sheets", e);
    }
  };

  const loadTeeSheetTemplates = async () => {
    // load templates
    try {
      const templateRes = await GetTeeSheetTemplate({ id: null }, false);

      if (templateRes.status !== StatusCode.OK) {
        updateState<IMainState>({ templates: [] }, StateType.MainState);
        return;
      }

      if (templateRes.status === StatusCode.OK) {
        updateState<IMainState>({ templates: templateRes.data }, StateType.MainState);
      }
    } catch (e) {
      console.log("GetTeeSheetTemplate ERR", e);
      updateState<IMainState>({ templates: [] }, StateType.MainState);
    }
  };

  const handleApplyTemplate = async (reApply: boolean = false) => {
    updateState<IMainState>({ applyTemplateActive: true }, StateType.MainState);

    const selectedTeeSheetTemplateTitles = state.teesheets
      .filter(teesheet => state.selectedTeeSheets.includes(teesheet.id))
      .map(value => value.tee_sheet_template_title);

    if ((selectedTeeSheetTemplateTitles.length === 1 && selectedTeeSheetTemplateTitles[0] !== null) || reApply) {
      updateState<IMainState>({ reApplyWarningVisible: true }, StateType.MainState);
    }

    // load templates
    try {
      const templateRes = await GetTeeSheetTemplate({ id: null }, false);

      if (templateRes.status !== StatusCode.OK) {
        updateState<IMainState>({ templates: [] }, StateType.MainState);
        return;
      }

      if (templateRes.status === StatusCode.OK) {
        updateState<IMainState>({ templates: templateRes.data }, StateType.MainState);
      }
    } catch (e) {
      console.log("GetTeeSheetTemplate ERR", e);
      updateState<IMainState>({ templates: [] }, StateType.MainState);
    }
  };

  const handleApplyTemplateRange = async () => {
    updateApplyRangeModal({ isOpen: true });

    // load templates
    try {
      const templateRes = await GetTeeSheetTemplate({ id: null }, false);

      if (templateRes.status !== StatusCode.OK) {
        updateState<IMainState>({ templates: [] }, StateType.MainState);
        return;
      }

      if (templateRes.status === StatusCode.OK) {
        updateState<IMainState>({ templates: templateRes.data }, StateType.MainState);
      }
    } catch (e) {
      console.log("GetTeeSheetTemplate ERR", e);
      updateState<IMainState>({ templates: [] }, StateType.MainState);
    }
  };

  async function printTeeSheetTemplate(teeSheetTemplateId: number) {
    const printRes = await GetTeeSheetTemplatePrint({ id: teeSheetTemplateId }, true);

    if (printRes.status !== StatusCode.OK) {
      return;
    }

    window.open().document.write(printRes.data as string);
  }

  const cancelApplyTemplate = () => {
    updateState<IMainState>(
      {
        applyTemplateActive: false,
        selectedTeeSheets: [],
        checked: false,
        addTemplatePopupVisible: false,
        reApplyWarningVisible: false,
        keepExistingBookings: true,
        keepExistingEvents: true,
      },
      StateType.MainState,
    );

    setWarningState(prevState => ({ ...prevState, showWarningModal: false, league: null, tournament: null }));
  };

  const onTemplateChange = (value: number) => {
    updateState<IMainState>({ selectedTemplate: value }, StateType.MainState);
  };

  const handleApply = async () => {
    const tee_sheet_ids = state.selectedTeeSheets;

    const template_id = state.selectedTemplate;

    const keep_existing_bookings = state.keepExistingBookings;

    const template_data = tee_sheet_ids.map(id => ({ tee_sheet_id: id, template_id: template_id }));

    try {
      const res = await PutApply({ template_data, keep_existing_bookings, keep_existing_events: state.keepExistingEvents }, true);

      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Error applying Tee Sheet Template")));
        cancelApplyTemplate(); // remove ID's if error trying to apply
      }

      if (res.status === StatusCode.OK) {
        void loadTeeSheets();
        dispatch(showSuccess(t("Succesfully applied Tee Sheet Template")));

        cancelApplyTemplate();
      }
    } catch (e) {
      console.log("PutApply ERR:", e);
    }
  };

  const handleApplyTemplatesToRange = () => {
    const updatedSelectedDates = [...applyRangeModal.selected_dates];

    for (let i = 0; i < applyRangeModal.selected_dates.length; i++) {
      const selectedDate = applyRangeModal.selected_dates[i];
      if (selectedDate.valid) {
        const templateProperty = `${selectedDate.day_of_week.toLocaleLowerCase()}_template_id`;
        updatedSelectedDates[i].template_id = applyRangeModal.apply_to_specific_days
          ? (applyRangeModal as any)[templateProperty]
          : applyRangeModal.default_template_id;
      }

      if (!updatedSelectedDates[i].template_id && selectedDate.valid) {
        updatedSelectedDates[i].valid = false;
        updatedSelectedDates[i].note = t("No template was found for this date");
      }
    }

    updateApplyRangeModal({ selected_dates: updatedSelectedDates });
  };

  const handleApplyRange = async () => {
    const template_data: IApplyData[] = [];

    for (let i = 0; i < applyRangeModal.selected_dates.length; i++) {
      const selectedDate = applyRangeModal.selected_dates[i];
      if (selectedDate.valid) {
        template_data.push({
          tee_sheet_id: selectedDate.tee_sheet_id,
          template_id: selectedDate.template_id,
        });
      }
    }

    try {
      const res = await PutApply({ template_data, keep_existing_bookings: applyRangeModal?.keep_existing_bookings, keep_existing_events: applyRangeModal?.keep_existing_events }, true);

      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Error applying tee sheet templates")));
        return;
      }

      void loadTeeSheets();
      closeApplyRangeModal();
      dispatch(showSuccess(t("Templates added to queue successfully")));
    } catch (e) {
      console.log("PutApplyRangeERR:", e);
    }
  };

  const primaryAction = {
    content: t("Apply Templates to Range"),
    action: () => handleApplyTemplateRange(),
  };

  const secondaryActions = [
    filteredTeeSheets && filteredTeeSheets.length > 0
      ? {
        content: t("Apply Template"),
        action: () => handleApplyTemplate(false),
        disabled: state.viewOpenCloseTimes || state.markHolidays ? true : false,
      }
      : {
        content: t("Create Tee Sheets"),
        action: () =>
          setCreateTeeSheetState(prevState => ({
            ...prevState,
            modalVisible: true,
            selectedDate: `${state.selectedQuickDateYear}-${state.selectedQuickDateMonth
              .toString()
              .padStart(2, "0")}`,
          })),
      },
  ];

  const onCourseChange = (value: number) => {
    updateState<IMainState>({ courseId: value, selectedTeeSheets: [] }, StateType.MainState);
  };

  function getDaysInPreviousMonth(date: Date) {
    return new Date(date.getFullYear(), date.getMonth()).getDay();
  }

  function getDaysInMonth(date: Date) {
    return 32 - new Date(date.getFullYear(), date.getMonth(), 32).getDate();
  }

  function getDaysInNextMonth(date: Date) {
    return 7 * 6 - (getDaysInPreviousMonth(date) + getDaysInMonth(date));
  }

  function shiftMonth(monthsToShiftBy: number) {
    const newDate = new Date(state.selectedCalendarDate.getFullYear(), state.selectedCalendarDate.getMonth());
    newDate.setMonth(newDate.getMonth() + monthsToShiftBy);

    updateState<IMainState>(
      {
        selectedCalendarDate: newDate,
        selectedQuickDateMonth: newDate.getMonth() + 1,
        selectedQuickDateYear: newDate.getFullYear(),
      },
      StateType.MainState,
    );
    setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
  }

  function handleDateInputChange(date: string) {
    setCreateTeeSheetState(prevState => ({ ...prevState, selectedDate: date }));
  }

  async function createTeeSheets() {
    //Add in the 15th day of the month for formatting
    const createRes = await PostTeeSheet(
      { date: createTeeSheetState.selectedDate.concat("-15"), course_id: state.courseId },
      true,
    );
    if (createRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating tee sheets")));
      return;
    }
    void loadTeeSheets();
    setCreateTeeSheetState(prevState => ({ ...prevState, modalVisible: false }));
    dispatch(showSuccess(t("Successfully created tee sheets")));
  }

  function handleToggle(viewOpenCloseTimes: boolean) {
    ReactDOM.unstable_batchedUpdates(() => {
      updateState<IMainState>({ selectedTeeSheets: [], viewOpenCloseTimes, markHolidays: false }, StateType.MainState);
      setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
    });
  }

  function handleHolidayToggle(markHolidays: boolean) {
    ReactDOM.unstable_batchedUpdates(() => {
      updateState<IMainState>(
        { selectedTeeSheets: !markHolidays ? [] : state.selectedTeeSheets, markHolidays, viewOpenCloseTimes: false },
        StateType.MainState,
      );
      setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
    });
  }

  async function handleApplyOpenWindow() {
    const openWindowRes = await PutTeeSheetOpenWindow(
      {
        open_time: openWindowState.openTime ?? null,
        close_time: openWindowState.closeTime ?? null,
        open_window_ids: openWindowState.selectedOpenWindows,
      },
      true,
    );
    if (openWindowRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating open windows")));
      return;
    }
    dispatch(showSuccess(t("Successfully updated open windows")));
    setOpenWindowState(prevState => ({
      ...prevState,
      editOpenWindowVisible: false,
      openTime: "06:00",
      closeTime: "20:00",
      selectedOpenWindows: [],
    }));
    await getOpenWindows();
  }

  function onSelectedOpenWindowChange(id: number) {
    if (id == null) {
      return;
    }
    const updatedOpenWindowSelection = [...openWindowState.selectedOpenWindows];
    const foundIndex = updatedOpenWindowSelection.indexOf(id);
    if (foundIndex === -1) {
      updatedOpenWindowSelection.push(id);
    } else {
      updatedOpenWindowSelection.splice(foundIndex, 1);
    }
    setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: updatedOpenWindowSelection }));
  }

  function selectAllOpenWindows() {
    const openWindows = { ...openWindowState.openWindows };
    const allOpenWindowIds = Object.keys(openWindows).map(keyName => {
      return openWindows[keyName]?.id as number;
    });
    setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: allOpenWindowIds }));
  }

  async function applyHolidays({ mark = false }) {
    const res = await PutTeeSheet({ tee_sheet_ids: state.selectedTeeSheets, holiday: mark }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error marking the dates as holidays")));
      return;
    }

    if (mark) {
      dispatch(showSuccess(t("Successfully marked the selected dates as holidays")));
    } else {
      dispatch(showSuccess(t("Successfully removed the holiday(s) from the calendar.")));
    }
    setState(prevState => ({
      ...prevState,
      holidaysAddPopupVisible: false,
      holidaysRemovePopupVisible: false,
      selectedTeeSheets: [],
    }));
    void loadTeeSheets();
  }

  const handleMonthChange = (value: number) => {
    const newDate = new Date(state.selectedQuickDateYear, value - 1);
    newDate.setMonth(newDate.getMonth());
    ReactDOM.unstable_batchedUpdates(() => {
      updateState<IMainState>({ selectedQuickDateMonth: value, selectedCalendarDate: newDate }, StateType.MainState);
      setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
    });
  };

  const handleYearChange = (value: number) => {
    const newDate = new Date(value, state.selectedQuickDateMonth - 1);
    newDate.setMonth(newDate.getMonth());
    ReactDOM.unstable_batchedUpdates(() => {
      updateState<IMainState>({ selectedQuickDateYear: value, selectedCalendarDate: newDate }, StateType.MainState);
      setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
    });
  };

  const handleDateNavigation = (e: React.MouseEvent, dateString: string) => {
    e.stopPropagation();

    const passingDate = new Date(dateString.replace(/-/g, "/"));
    history.push("/admin/teesheet", { params: { teeSheetDate: passingDate } });
  };

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:00"];
  }

  useEffect(() => {
    if (applyRangeModal.date_range_start && applyRangeModal.date_range_end) {
      const date_range = formatDateRange(applyRangeModal.date_range_start, applyRangeModal.date_range_end);
      updateApplyRangeModal({ date_range: date_range });
    }
  }, [applyRangeModal.date_range_start, applyRangeModal.date_range_end]);

  function setDateRangeStart(date: Date) {
    updateApplyRangeModal({ date_range_start: date });
  }

  function setDateRangeEnd(date: Date) {
    updateApplyRangeModal({ date_range_end: date });
  }

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked } = e.target;
    const updateSelectedValues: any[] = [...applyRangeModal.days_of_week];
    if (checked) {
      updateSelectedValues.push(value);
    } else {
      const removeIndex = updateSelectedValues?.findIndex(valueId => valueId === value);
      if (removeIndex !== -1) {
        updateSelectedValues.splice(removeIndex, 1);
      }
    }
    updateApplyRangeModal({ days_of_week: updateSelectedValues });
  }

  function handleAddDates() {
    const dates = applyRangeModal.selected_dates.slice();

    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    const startDate = clone(applyRangeModal.date_range_start);
    const endDate = clone(applyRangeModal.date_range_end);

    for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      if (dates.length >= 60) {
        dispatch(showError(t("Can only select 60 days at a time")));
        break;
      }

      const teesheet = state.teesheets?.find(
        teesheet => teesheet.date === moment(d).format("YYYY-MM-DD") && teesheet.course_id === state.courseId,
      );
      const dateExists = dates.find(date => date.date === moment(d).format("YYYY-MM-DD"));
      if (
        !teesheet &&
        !dateExists &&
        applyRangeModal.days_of_week.find(day => day === days[d.getDay()].toLocaleLowerCase())
      ) {
        dates.push({
          date: moment(d).format("YYYY-MM-DD"),
          tee_sheet_id: null,
          template_id: null,
          day_of_week: days[d.getDay()],
          valid: false,
          note: t("Tee sheet does not exist for this date"),
        });
        continue;
      } else if (
        teesheet &&
        !dateExists &&
        applyRangeModal.days_of_week.find(day => day === teesheet.day_of_week.toLocaleLowerCase())
      ) {
        dates.push({
          date: moment(d).format("YYYY-MM-DD"),
          tee_sheet_id: teesheet.id,
          template_id: null,
          day_of_week: days[d.getDay()],
          valid: true,
          note: teesheet.tee_sheet_template_title
            ? `${t("Current template:")} ${teesheet.tee_sheet_template_title}`
            : "",
        });
      }
    }

    dates.sort((date1, date2) => {
      return date1.date.localeCompare(date2.date);
    });

    updateApplyRangeModal({ selected_dates: dates });
  }

  function handleRemoveDate(index: number) {
    const dates = applyRangeModal.selected_dates.slice();
    dates.splice(index, 1);

    updateApplyRangeModal({ selected_dates: dates });
  }

  function applyRangeModalTitle() {
    switch (applyRangeModal.step) {
      case 1:
        return t("Select Range");
      case 2:
        return t("Select Templates");
      case 3:
        return t("Review");
    }
  }

  function applyRangeModalOnOK() {
    switch (applyRangeModal.step) {
      case 1:
        updateApplyRangeModal({ step: 2 });
        break;
      case 2:
        handleApplyTemplatesToRange();
        updateApplyRangeModal({ step: 3 });
        break;
      case 3:
        void handleApplyRange();
        break;
    }
  }

  function applyRangeModalOnCancel() {
    switch (applyRangeModal.step) {
      case 1:
        closeApplyRangeModal();
        break;
      case 2:
        updateApplyRangeModal({ step: 1 });
        break;
      case 3:
        updateApplyRangeModal({ step: 2 });
        break;
    }
  }

  async function downloadBookingsPdf(teeSheetId: number) {
    const downloadRes = await GetBookingsPdf({ tee_sheet_id: teeSheetId }, true);

    if (downloadRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error downloading bookings PDF")));

      return;
    }

    window.open().document.write(downloadRes.data as string);
  }

  function getDefaultCheckboxes(): boolean[] {
    const defaults = [];

    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    for (let i = 0; i < days.length; i++) {
      if (applyRangeModal.days_of_week?.find(day => day === days[i].toLocaleLowerCase())) {
        defaults.push(true);
      } else {
        defaults.push(false);
      }
    }
    return defaults;
  }

  async function handleCheckForEvents() {
    if (state?.keepExistingEvents) {
      void handleApply();
    } else {
      let tournamentId;
      let leagueId;

      const params = {
        tee_sheet_id: state.selectedTeeSheets[0],
        extended: true,
        extended_bookings: true,
      };

      const teeTimesRes = await GetTeeTime(params, true);

      if (teeTimesRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error checking for events")));
        return;
      }

      const teeTimes = teeTimesRes.data;

      teeTimes.map((teeTime: any) => {
        if (teeTime.tournament_id) {
          tournamentId = teeTime.tournament_id;
        }

        if (teeTime.league_id) {
          leagueId = teeTime.league_id;
        }
      });

      if (tournamentId) {
        const tournamentRes = await GetTournament({ id: tournamentId }, true);

        if (tournamentRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error getting tournament information")));
          return;
        }

        setWarningState(prevState => ({
          ...prevState,
          tournament: tournamentRes.data[0],
        }));
      }

      if (leagueId) {
        const leagueRes = await GetLeague({ id: leagueId }, true);

        if (leagueRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error getting league information")));
          return;
        }

        setWarningState(prevState => ({ ...prevState, league: leagueRes.data[0] }));
      }

      if (leagueId || tournamentId) {
        setWarningState(prevState => ({ ...prevState, showWarningModal: true }));
        setState(prevState => ({
          ...prevState,
          reApplyWarningVisible: false,
          applyTemplateActive: false,
        }));
      } else {
        void handleApply();
      }
    }
  }

  function handleCloseEventWarning() {
    setWarningState(prevState => ({ ...prevState, showWarningModal: false, league: null, tournament: null }));
  }

  return (
    <Page
      title={t("Calendar")}
      subtitle={t("View tee sheets applied throughout the season")}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            // {
            //   type: "handler",
            //   label: t("Create Tee Sheets"),
            //   handler: () => setCreateTeeSheetState(prevState => ({ ...prevState, modalVisible: true })),
            // },
            {
              type: "conditional",
              label: t("Holidays"),
              active: state.markHolidays,
              setActive: handleHolidayToggle,
              subOptions: [
                {
                  type: "handler",
                  label: t("Unmark Holidays"),
                  handler: () => setState(prevState => ({ ...prevState, holidaysRemovePopupVisible: true })),
                },
                {
                  type: "handler",
                  label: t("Add Holidays"),
                  handler: () => setState(prevState => ({ ...prevState, holidaysAddPopupVisible: true })),
                },
              ],
            },
            {
              type: "conditional",
              label: t("Open Windows"),
              active: state.viewOpenCloseTimes,
              setActive: handleToggle,
              subOptions: [
                {
                  type: "handler",
                  label: t("Edit Open Windows"),
                  handler: () => setOpenWindowState(prevState => ({ ...prevState, editOpenWindowVisible: true })),
                },
                {
                  type: "handler",
                  label: t("Select All"),
                  handler: selectAllOpenWindows,
                  disableAutoClose: true,
                },
                {
                  type: "handler",
                  label: t("Unselect All"),
                  handler: () => setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] })),
                  disableAutoClose: true,
                },
              ],
            },
          ],
        },
      }}
    >
      {state.teesheets?.length > 0 &&
        state.courseId &&
        !isNaN(state.courseId) &&
        (() => {
          const courses = facilityStore?.facility?.courses ?? [];

          return (
            <>
              {courses.length > 1 ? (
                <div className="courses-dropdown-container">
                  <Select className="courses-dropdown" defaultValue={state.courseId} onChange={onCourseChange}>
                    {courses.map((course: any) => {
                      return (
                        <Option value={course?.id} key={course?.id}>
                          {course?.full_name}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              ) : null}

              <div className="calendar-quick-date-selection">
                <button className="navigation-button" onClick={() => shiftMonth(-1)}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <div className="calendar-quick-date-picker">
                  <div>
                    <Select defaultValue={state.selectedQuickDateMonth} onChange={handleMonthChange}>
                      {state.months?.map(month => {
                        return (
                          <Option value={month.id} key={month.id}>
                            {month.month}
                          </Option>
                        );
                      })}
                    </Select>
                  </div>

                  <div>
                    <Select defaultValue={state.selectedQuickDateYear} onChange={handleYearChange}>
                      {state.years?.map(year => {
                        return (
                          <Option value={year.id} key={year.id}>
                            {year.year}
                          </Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                <button className="navigation-button" onClick={() => shiftMonth(1)}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>

              {filteredTeeSheets && filteredTeeSheets.length > 0 ? (
                <>
                  <div className="grid-container">
                    {moment?.weekdays()?.map((day, i) => {
                      return (
                        <div key={i} className="day-title">
                          <p>{day?.substring(0, 3)}</p>
                        </div>
                      );
                    })}
                    {Array.from(Array(getDaysInPreviousMonth(state.selectedCalendarDate)).keys()).map(i => {
                      return <div key={i} className="grid-item"></div>;
                    })}
                    {filteredTeeSheets.map((teesheet, i) => {
                      let deleted = false;

                      if (teesheet.tee_sheet_template_id) {
                        //check if deleted (id is not in templates)
                        const templateExists = state.templates?.find(
                          template => template.id === teesheet.tee_sheet_template_id,
                        );

                        if (!templateExists) {
                          deleted = true;
                        }
                      }

                      return (
                        <div key={i}>
                          <RightClickMenu
                            key={i}
                            sections={[
                              [
                                {
                                  icon: "pencil",
                                  title: t("Edit Template"),
                                  action: () =>
                                    history.push(
                                      "/admin/settings/tee-sheet/template/" + String(teesheet.tee_sheet_template_id),
                                    ),
                                  disabled: !teesheet.tee_sheet_template_title || deleted,
                                },
                                {
                                  icon: "print",
                                  title: t("Print Template"),
                                  action: () => printTeeSheetTemplate(teesheet.tee_sheet_template_id),
                                  disabled: !teesheet.tee_sheet_template_title || deleted,
                                },
                              ],
                            ]}
                          >
                            <div
                              className={`grid-item teesheet ${state.selectedTeeSheets.some(selectedId => selectedId === teesheet?.id) ||
                                openWindowState.selectedOpenWindows.some(
                                  selectedId => selectedId === openWindowState.openWindows[teesheet.date]?.id,
                                )
                                ? "selected-teesheet"
                                : "unselected-teesheet"
                                }`}
                              onClick={
                                state.viewOpenCloseTimes
                                  ? () =>
                                    onSelectedOpenWindowChange(
                                      openWindowState.openWindows[teesheet.date]?.id as number,
                                    )
                                  : () => onSelectedTeeSheetChange(teesheet?.id, teesheet)
                              }
                              key={i}
                            >
                              {!!teesheet?.holiday && (
                                <div className="holiday">
                                  <Badge type="primary" size="small">
                                    {t("Holiday")}
                                  </Badge>
                                </div>
                              )}
                              {moment(teesheet.date).isSameOrAfter(
                                moment(new Date()).format("YYYY-MM-DD").toString(),
                                "day",
                              ) && (
                                  <FontAwesomeIcon
                                    icon={["far", "pencil"]}
                                    className="edit-icon"
                                    onClick={() => handleApplyTemplate(!!teesheet.tee_sheet_template_id)}
                                  />
                                )}
                              <div className="day-of-month" onClick={e => handleDateNavigation(e, teesheet.date)}>
                                <p>{i + 1}</p>
                              </div>
                              <div className="grid-content">
                                {state.viewOpenCloseTimes ? (
                                  <>
                                    <div className="open-window-container">
                                      <span>{t("Open:")} </span>
                                      <span>
                                        {openWindowState.openWindows[teesheet.date]?.open_time
                                          ? moment(
                                            openWindowState.openWindows[teesheet.date]?.open_time as MomentInput,
                                            "hh:mm:ss",
                                          ).format("h:mm A")
                                          : ""}
                                      </span>
                                    </div>
                                    <div className="open-window-container">
                                      <span>{t("Close:")} </span>
                                      <span>
                                        {openWindowState.openWindows[teesheet.date]?.close_time
                                          ? moment(
                                            openWindowState.openWindows[teesheet.date]?.close_time as MomentInput,
                                            "hh:mm:ss",
                                          ).format("h:mm A")
                                          : ""}
                                      </span>
                                    </div>
                                  </>
                                ) : deleted ? (
                                  <div>
                                    <Badge type="gray" size="small">
                                      {"Template deleted"}
                                    </Badge>
                                  </div>
                                ) : teesheet.tee_sheet_template_title ? (
                                  <div className="template-title-wrap">
                                    <span className="template-title">{teesheet.tee_sheet_template_title}</span>
                                    {teesheet.tee_sheet_template_version && (
                                      <span className="calendar-version">
                                        Version {teesheet.tee_sheet_template_version}
                                      </span>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </RightClickMenu>
                        </div>
                      );
                    })}
                    {Array.from(Array(getDaysInNextMonth(state.selectedCalendarDate)).keys()).map(i => {
                      return <div key={i} className="grid-item"></div>;
                    })}
                  </div>
                </>
              ) : (
                <Callout
                  type="error"
                  title={t("There are no Tee Sheets for this month and course")}
                  content={t("To create Tee Sheets click Create Tee Sheet")}
                />
              )}

              <Sheet
                open={state.applyTemplateActive}
                closable
                title={t("Select Template")}
                onCancel={cancelApplyTemplate}
                onOk={handleCheckForEvents}
                cancelText={t("Clear")}
                okText={t("Apply")}
                size="small"
                overflow
                okDisabled={state.selectedTemplate === null || state.selectedTeeSheets.length === 0}
              >
                {state.reApplyWarningVisible ? (
                  <FormLayout>
                    <FormLayout.Group>
                      <Callout
                        type="warning"
                        title={t("Re-applying Template")}
                        content={t(
                          "Bookings may exist on the selected date. A PDF of existing bookings will be available for download from the PDFs page",
                        )}
                        actions={{
                          primary: {
                            label: t("Open PDFs page"),
                            onClick: () => window.open("/admin/settings/pdf"),
                          },
                        }}
                      />
                    </FormLayout.Group>
                  </FormLayout>
                ) : null}
                <SelectNew
                  id={"apply-template-select-template"}
                  onChange={onTemplateChange}
                  value={state.selectedTemplate}
                  options={state.templates
                    ?.filter(template => template?.course_id === state.courseId)
                    ?.map(template => ({
                      id: template.id,
                      label: template.name,
                    }))}
                  dropdownSize="small"
                  label={t("Template")}
                />
                {state.reApplyWarningVisible ? (
                  <>
                    <br />
                    <FormLayout>
                      <FormLayout.Group>
                        <Checkbox
                          label={t("Keep Existing Bookings")}
                          checked={state.keepExistingBookings}
                          onChange={e =>
                            updateState<IMainState>({ keepExistingBookings: e.target.checked }, StateType.MainState)
                          }
                          size="medium"
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <Checkbox
                          label={t("Keep Existing Events")}
                          checked={state.keepExistingEvents}
                          onChange={e =>
                            updateState<IMainState>({ keepExistingEvents: e.target.checked }, StateType.MainState)
                          }
                          size="medium"
                        />
                      </FormLayout.Group>
                    </FormLayout>
                  </>
                ) : null}
              </Sheet>

              <Sheet
                open={openWindowState.editOpenWindowVisible}
                closable
                overflow
                title={t("Edit Open Windows")}
                onCancel={() =>
                  setOpenWindowState(prevState => ({
                    ...prevState,
                    editOpenWindowVisible: false,
                    openTime: "06:00",
                    closeTime: "20:00",
                  }))
                }
                onOk={handleApplyOpenWindow}
                cancelText={t("Close")}
                okText={t("Save")}
                size="small"
                okDisabled={
                  !openWindowState.openTime ||
                    !openWindowState.closeTime ||
                    openWindowState?.selectedOpenWindows?.length === 0
                    ? true
                    : false
                }
              >
                <Form>
                  <FormLayout>
                    <FormLayout.Group>
                      <TimePick
                        value={openWindowState.openTime}
                        onChange={timeString => setOpenWindowState(prev => ({ ...prev, openTime: timeString }))}
                        label={t("Open Time")}
                        size="large"
                        status={openWindowState.openTime === undefined ? "warning" : undefined}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <TimePick
                        value={openWindowState.closeTime}
                        onChange={timeString => setOpenWindowState(prev => ({ ...prev, closeTime: timeString }))}
                        label={t("Close Time")}
                        size="large"
                        status={openWindowState.closeTime === undefined ? "warning" : undefined}
                      />
                    </FormLayout.Group>
                  </FormLayout>
                </Form>
                {openWindowState.selectedOpenWindows?.length === 0 ? (
                  <Callout
                    type="error"
                    title={t("No Dates Selected")}
                    content={t("Select the dates on the calendar and try again")}
                  />
                ) : null}
              </Sheet>
            </>
          );
        })()}

      <Sheet
        open={createTeeSheetState.modalVisible}
        closable
        title={t("Create Tee Sheets")}
        onCancel={() => setCreateTeeSheetState(prevState => ({ ...prevState, modalVisible: false }))}
        onOk={createTeeSheets}
        cancelText={t("Close")}
        okText={t("Create")}
        size="small"
        overflow
      >
        {createTeeSheetState.modalVisible && (
          <MonthPicker
            onApply={handleDateInputChange}
            label={t("Select Month and Year")}
            startMonth={state.selectedQuickDateMonth}
            startYear={state.selectedQuickDateYear}
          />
        )}
      </Sheet>

      <Sheet
        open={warningState.showWarningModal}
        closable
        onCancel={handleCloseEventWarning}
        onOk={handleApply}
        cancelText={t("Cancel")}
        okText={t("Yes, replace tee sheet")}
        size="small"
        overflow
      >
        <div className="p-4">
          <div className="calendar-event-warning-icon">
            <FontAwesomeIcon icon={faCircleExclamation} />
          </div>

          <div className="mt-2">
            <p className="calendar-event-warning-title">{t("An event already exists on this tee sheet")}</p>{" "}
            <p className="calendar-event-warning-text mb-4">
              {t("Applying this template will override the existing tee times on")}{" "}
              <span className="calendar-event-warning-text-bold">{formatDateFromString(warningState.eventDate)}</span>{" "}
              for:
            </p>{" "}
            <ul className="calendar-event-warning-list mb-4">
              {warningState.tournament ? (
                <li className="calendar-event-warning-text-bold">{warningState.tournament.name}</li>
              ) : null}
              {warningState.league ? (
                <li className="calendar-event-warning-text-bold">{warningState.league.name}</li>
              ) : null}
            </ul>
            <p className="calendar-event-warning-text">
              {" "}
              {t("Do you want to replace the current tee sheet with your new template?")}
            </p>
          </div>
        </div>
      </Sheet>

      <Popup
        open={state.holidaysAddPopupVisible}
        okDisabled={state.selectedTeeSheets?.length === 0 ? true : false}
        onCancel={() => setState(prevState => ({ ...prevState, holidaysAddPopupVisible: false }))}
        onOk={() => applyHolidays({ mark: true })}
        okText={t("Mark as Holiday")}
        type="info"
        title={t("Add Holidays")}
        description={t("Mark the selected dates as holidays?")}
      />

      <Popup
        open={state.holidaysRemovePopupVisible}
        okDisabled={state.selectedTeeSheets?.length === 0 ? true : false}
        onCancel={() => setState(prevState => ({ ...prevState, holidaysRemovePopupVisible: false }))}
        onOk={() => applyHolidays({ mark: false })}
        okText={t("Unmark Holiday")}
        type="warning"
        title={t("Unmark Holiday")}
        description={t("Unmark the holiday from these dates?")}
      />

      {/* <Popup
        open={state.addTemplatePopupVisible}
        onCancel={() => setState(prevState => ({ ...prevState, addTemplatePopupVisible: false }))}
        onOk={handleApply}
        stacked
        okText={t("Apply")}
        type="warning"
        title={t("Apply Templates?")}
        description={t("Once submitted, your request will be sent off and processed. Please check back in a bit to confirm your templates have been applied")}
      /> */}

      <Sheet
        open={applyRangeModal.isOpen}
        size="medium"
        title={applyRangeModalTitle()}
        onCancel={applyRangeModalOnCancel}
        onOk={applyRangeModalOnOK}
        okText={applyRangeModal?.step !== 3 ? t("Continue") : t("Apply")}
        okDisabled={
          (applyRangeModal.step === 1 && applyRangeModal.selected_dates.length <= 0) ||
          (applyRangeModal.step === 2 &&
            ((!applyRangeModal.apply_to_all_days && !applyRangeModal.apply_to_specific_days) ||
              (applyRangeModal.apply_to_all_days && applyRangeModal.default_template_id === null) ||
              (applyRangeModal.apply_to_specific_days &&
                ((applyRangeModal.monday_template_id === null &&
                  applyRangeModal.selected_dates.findIndex(date => date.day_of_week.toLocaleLowerCase() === "monday") >=
                  0) ||
                  (applyRangeModal.tuesday_template_id === null &&
                    applyRangeModal.selected_dates.findIndex(
                      date => date.day_of_week.toLocaleLowerCase() === "tuesday",
                    ) >= 0) ||
                  (applyRangeModal.wednesday_template_id === null &&
                    applyRangeModal.selected_dates.findIndex(
                      date => date.day_of_week.toLocaleLowerCase() === "wednesday",
                    ) >= 0) ||
                  (applyRangeModal.thursday_template_id === null &&
                    applyRangeModal.selected_dates.findIndex(
                      date => date.day_of_week.toLocaleLowerCase() === "thursday",
                    ) >= 0) ||
                  (applyRangeModal.friday_template_id === null &&
                    applyRangeModal.selected_dates.findIndex(
                      date => date.day_of_week.toLocaleLowerCase() === "friday",
                    ) >= 0) ||
                  (applyRangeModal.saturday_template_id === null &&
                    applyRangeModal.selected_dates.findIndex(
                      date => date.day_of_week.toLocaleLowerCase() === "saturday",
                    ) >= 0) ||
                  (applyRangeModal.sunday_template_id === null &&
                    applyRangeModal.selected_dates.findIndex(
                      date => date.day_of_week.toLocaleLowerCase() === "sunday",
                    ) >= 0)))))
        }
        cancelText={applyRangeModal?.step !== 1 ? t("Back") : t("Cancel")}
        closable
      >
        {/* Pick Dates */}
        {applyRangeModal.step === 1 ? (
          <FormLayout>
            <FormLayout.Group>
              <DatePickerInput
                label={t("Range")}
                months={windowSize.width > TABLET_WIDTH ? 2 : 1}
                position="left"
                startingDate={applyRangeModal.date_range_start}
                setStartingDate={setDateRangeStart}
                endingDate={applyRangeModal.date_range_end}
                setEndingDate={setDateRangeEnd}
                disablePastDays
              />
              <div className="flex gap-2">
                <FilterDropdown
                  label={t("Days to Add")}
                  data={dayOfWeekOptions}
                  dataLabelProperty={"label"}
                  checkBoxId={"days_of_week"}
                  dataValueProperty="value"
                  onCheckBoxChange={onCheckBoxChange}
                  onClear={() => updateApplyRangeModal({ days_of_week: [] })}
                  onAddAll={() =>
                    updateApplyRangeModal({
                      days_of_week: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
                    })
                  }
                  defaultCheckBoxes={getDefaultCheckboxes()}
                />
                <Button type="primary" onClick={handleAddDates}>
                  {t("Add")}
                </Button>
              </div>
            </FormLayout.Group>
            {applyRangeModal.selected_dates.filter(date => date.valid === false)?.length > 0 ? (
              <FormLayout.Group>
                <Callout
                  type="info"
                  title={t("Invalid Dates Selected")}
                  content={`${applyRangeModal.selected_dates.filter(date => date.valid === false)?.length} ${t(
                    "invalid dates have been selected. Templates will not be applied to these dates. Details are listed below",
                  )}`}
                />
              </FormLayout.Group>
            ) : null}
            <FormLayout.Group>
              <DataTable
                columns={[
                  { label: t("Date") },
                  { label: t("Day of Week") },
                  { label: "" },
                  { label: "", width: "5%" },
                  {
                    label: "",
                    width: "5%",
                    content: (
                      <Button
                        onClick={() => updateApplyRangeModal({ selected_dates: [] })}
                        className="text-gray-600"
                        type="text"
                      >
                        {t("Clear")}
                      </Button>
                    ),
                  },
                ]}
              >
                {applyRangeModal.selected_dates.map((date, index) => (
                  <tr key={index}>
                    <td>
                      <div className="flex gap-2 align-center">
                        {date.valid ? (
                          <FontAwesomeIcon
                            className="tee-sheet-date-icon-success mt-1"
                            size="1x"
                            icon={faCircleCheck}
                          />
                        ) : (
                          <FontAwesomeIcon className="tee-sheet-date-icon-error mt-1" size="1x" icon={faCircleXmark} />
                        )}
                        {date.date}
                      </div>
                    </td>
                    <td>{date.day_of_week}</td>
                    <td>{date.note}</td>
                    <td className="text-center">
                      {date.valid && date.note !== "" ? (
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id={"download_bookings_info"}>
                              {t("Download a PDF of existing bookings on this date")}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon
                            onClick={() => downloadBookingsPdf(date.tee_sheet_id)}
                            icon={["far", "download"]}
                            size="1x"
                            className="cursor-pointer"
                          />
                        </OverlayTrigger>
                      ) : null}
                    </td>
                    <td className="text-center">
                      <FontAwesomeIcon
                        onClick={() => handleRemoveDate(index)}
                        icon={faTrashCanXmark}
                        size="1x"
                        className="cursor-pointer"
                      />
                    </td>
                  </tr>
                ))}
              </DataTable>
            </FormLayout.Group>
          </FormLayout>
        ) : null}
        {/* Pick Templates */}
        {applyRangeModal.step === 2 ? (
          <FormLayout>
            <FormLayout.Group>
              <Checkbox
                label={t("Apply One Template to All Selected Dates")}
                checked={applyRangeModal.apply_to_all_days}
                onChange={e =>
                  updateApplyRangeModal({
                    apply_to_all_days: e.target.checked,
                    apply_to_specific_days: e.target.checked ? false : applyRangeModal.apply_to_specific_days,
                  })
                }
                size="medium"
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                label={t("Apply Different Templates to Specific Days of the Week")}
                checked={applyRangeModal.apply_to_specific_days}
                onChange={e =>
                  updateApplyRangeModal({
                    apply_to_specific_days: e.target.checked,
                    apply_to_all_days: e.target.checked ? false : applyRangeModal.apply_to_all_days,
                  })
                }
                size="medium"
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                label={t("Keep Existing Bookings")}
                checked={applyRangeModal?.keep_existing_bookings}
                onChange={e => updateApplyRangeModal({ keep_existing_bookings: e.target.checked })}
                size="medium"
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                label={t("Keep Existing Events")}
                checked={applyRangeModal?.keep_existing_events}
                onChange={e => updateApplyRangeModal({ keep_existing_events: e.target.checked })}
                size="medium"
              />
            </FormLayout.Group>
            {applyRangeModal.apply_to_all_days ? (
              <FormLayout.Group>
                <Select label={t("Template")} onChange={value => updateApplyRangeModal({ default_template_id: value })}>
                  {state.templates ? (
                    <>
                      {state.templates
                        .filter(template => template?.course_id === state.courseId)
                        .map(template => {
                          return (
                            <Option value={template.id} key={template.id}>
                              {template.name}
                            </Option>
                          );
                        })}
                    </>
                  ) : (
                    <Option value={"spinner"}>
                      <div style={{ height: "40px" }}>
                        <Spin />
                      </div>
                    </Option>
                  )}
                </Select>
              </FormLayout.Group>
            ) : null}
            {applyRangeModal.apply_to_specific_days ? (
              <>
                {dayOfWeekOptions
                  ?.filter(
                    day =>
                      applyRangeModal.selected_dates?.findIndex(
                        date => date.day_of_week.toLocaleLowerCase() === day.value,
                      ) >= 0,
                  )
                  ?.map((day: { value: string; label: string }) => (
                    <FormLayout.Group key={day}>
                      <Select
                        label={`${valueToString(day.label)} Template` /* TODO: Translation */}
                        onChange={(value: string) => updateApplyRangeModal({ [`${day.value}_template_id`]: value })}
                      >
                        {state.templates ? (
                          <>
                            {state.templates
                              .filter(template => template?.course_id === state.courseId)
                              .map(template => {
                                return (
                                  <Option value={template.id} key={template.id}>
                                    {template.name}
                                  </Option>
                                );
                              })}
                          </>
                        ) : (
                          <Option value={"spinner"}>
                            <div style={{ height: "40px" }}>
                              <Spin />
                            </div>
                          </Option>
                        )}
                      </Select>
                    </FormLayout.Group>
                  ))}
              </>
            ) : null}
          </FormLayout>
        ) : null}
        {/* Review */}
        {applyRangeModal.step === 3 ? (
          <div>
            {applyRangeModal.selected_dates.filter(date => date.valid === false)?.length > 0 ? (
              <FormLayout.Group>
                <Callout
                  type="info"
                  title={t("Invalid Dates Selected")}
                  content={`${applyRangeModal.selected_dates.filter(date => date.valid === false)?.length} ${t(
                    "invalid dates have been selected. Templates will not be applied to these dates. Details are listed below",
                  )}`}
                />
              </FormLayout.Group>
            ) : null}
            <DataTable
              columns={[
                { label: t("Date") },
                { label: t("Day of Week") },
                { label: t("Template") },
                { label: "" },
                { label: "", width: "5%" },
              ]}
            >
              {applyRangeModal.selected_dates.map((date, index) => (
                <tr key={index}>
                  <td>
                    <div className="flex gap-2 align-center">
                      {date.valid ? (
                        <FontAwesomeIcon className="tee-sheet-date-icon-success mt-1" size="1x" icon={faCircleCheck} />
                      ) : (
                        <FontAwesomeIcon className="tee-sheet-date-icon-error mt-1" size="1x" icon={faCircleXmark} />
                      )}
                      {date.date}
                    </div>
                  </td>
                  <td>{date.day_of_week}</td>
                  <td>{state.templates?.find(template => template.id === date.template_id)?.name}</td>
                  <td>{date.note}</td>
                  <td>
                    <FontAwesomeIcon
                      onClick={() => handleRemoveDate(index)}
                      icon={faTrashCanXmark}
                      size="1x"
                      className="cursor-pointer"
                    />
                  </td>
                </tr>
              ))}
            </DataTable>
          </div>
        ) : null}
      </Sheet>
    </Page>
  );
};

export default TeeSheetCalendar;
